/**
 * Tipos y constantes de la gestión de usuarios (#70, destraba #45).
 *
 * Vive en un `.ts` y no en el `.tsx` de la vista por la convención del
 * andamio: un archivo `.tsx` exporta componentes y nada más, si no se rompe el
 * hot reload y el linter lo marca.
 *
 * Por qué `UserAccount` extiende `User` en vez de salir entero del OpenAPI
 * ----------------------------------------------------------------------
 * `User` (o sea `components['schemas']['UserOut']`) es el listado de sólo
 * lectura de `/auth/users`, y sale de `schema.d.ts`. `/api/v1/users` devuelve
 * lo mismo **más `auth_mode`**, que todavía no está en `schema.d.ts` porque
 * regenerarlo ahora arrastraría los endpoints a medio terminar de los otros
 * módulos de #70 (ese punto del issue —"schema.d.ts regenerado y sin rutas
 * fantasma"— se cierra cuando estén los cuatro).
 *
 * En cuanto se regenere, este archivo se reduce a un alias:
 *
 *     export type UserAccount = components['schemas']['UserAccountOut'];
 *
 * y nada más de la vista cambia. Mientras tanto el tipo es estricto y no hay
 * ningún `any`: lo único que se está adelantando es el nombre de dos campos.
 */
import type { Role, User } from '@/api/types';
import type { MessageKey } from '@/i18n/messages';

/**
 * Contra qué autentica la cuenta.
 *
 * `password` es una credencial local de XYVONA; `oidc`, el directorio
 * corporativo del cliente. Conviven en la misma marca a propósito: el
 * back-office propio, las marcas sin directorio y el acceso de emergencia
 * cuando el IdP del cliente se cae necesitan una contraseña local aunque el
 * resto del tenant esté federado.
 *
 * **La autorización es local siempre.** Azure AD sabe quién es
 * `maria@laboratorio.com`; no sabe que en XYVONA es analista de `labfarma`.
 * Por eso el rol se administra en esta pantalla incluso para las cuentas
 * federadas.
 */
export type AuthMode = 'password' | 'oidc';

/** Un usuario del tenant. Nunca trae el hash de la contraseña. */
export interface UserAccount extends User {
  auth_mode: AuthMode;
}

/** Respuesta del alta. La contraseña temporal viaja **una sola vez**. */
export interface UserCreated {
  user: UserAccount;
  temporary_password: string | null;
  audit_seq: number;
}

/** Respuesta del reseteo de contraseña. */
export interface PasswordReset {
  user_id: number;
  temporary_password: string | null;
  sessions_revoked: number;
  audit_seq: number;
}

/** Cuerpo del alta. Sin contraseña: la genera el servidor. */
export interface UserDraft {
  email: string;
  full_name: string | null;
  role: Role;
}

/** Filtros del listado. Ninguno es el tenant: ese sale del JWT. */
export interface UserFilters {
  /** Rol exacto, o `null` para todos. */
  role: Role | null;
  /** Estado, o `null` para todos. */
  active: boolean | null;
  /** Texto libre sobre correo y nombre. Cadena vacía = sin búsqueda. */
  q: string;
}

export const EMPTY_FILTERS: UserFilters = { role: null, active: null, q: '' };

/** Roles que se pueden asignar, de menor a mayor. */
export const ASSIGNABLE_ROLES = ['viewer', 'analyst', 'admin'] as const;

export const AUTH_MODE_LABEL_KEYS: Record<AuthMode, MessageKey> = {
  password: 'users.authMode.password',
  oidc: 'users.authMode.oidc',
};

export function isRole(value: string): value is Role {
  return (ASSIGNABLE_ROLES as readonly string[]).includes(value);
}
